import React from 'react';
import PropTypes from 'prop-types';
import useFulfilmentLocation from '@afosto/queries/fulfilment/useFulfilmentLocation';
import useHandlingList from '@afosto/queries/fulfilment/useHandlingList';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { Link, useHistory } from 'react-router-dom';
import { FormattedHTMLMessage } from 'react-intl';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import Icon from '@afosto/app-components-react/Icon';
import formatDate from '@afosto/utils/date/formatDate';
import translations from '../translations';

const HandlingListCard = ({ handlingList }) => {
  const intl = useIntl();
  const { id, is_finished: isFinished } = handlingList ?? {};
  const confirm = useConfirm();
  const history = useHistory();
  const { data: handlingListData } = useHandlingList(id);
  const { data: location } = useFulfilmentLocation(handlingList?.location, {
    enabled: handlingList?.location,
  });
  const positions = (handlingListData?.items ?? []).reduce((acc, { position }) => {
    if (!acc.includes(position)) {
      return [...acc, position];
    }

    return acc;
  }, []);

  const handleCardClick = async event => {
    if (isFinished) {
      event.preventDefault();
      const response = await confirm({
        title: intl.formatMessage(translations.confirmViewHandlingList.title),
        description: intl.formatMessage(translations.confirmViewHandlingList.description),
        buttons: [
          {
            label: intl.formatMessage(translations.confirmViewHandlingList.actions.close),
            response: 'close',
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.confirmViewHandlingList.actions.view),
            response: 'view',
            type: 'submit',
            variant: 'contained',
            color: 'primary',
          },
        ],
        catchOnCancel: false,
      });
      if (response === 'view') {
        history.push(`/handling-lists/${id}`);
      }
    }
  };

  return (
    <Card>
      <Box display="flex" alignItems="center">
        <Box flexGrow="1">
          <CardActionArea
            component={Link}
            to={`/handling-lists/${handlingList.id}/process`}
            onClick={handleCardClick}
          >
            <CardContent>
              <Box display="flex">
                <Box flex={1}>
                  <ListItemText
                    primary={handlingList.number?.toUpperCase() || handlingList.id}
                    secondary={handlingList?.due_at ? formatDate(handlingList.due_at) : null}
                  />
                </Box>
                <Box mx={1}>
                  <IconButton onClick={() => history.push(`/handling-lists/${handlingList?.id}`)}>
                    <Icon icon={['fal', 'eye']} />
                  </IconButton>
                </Box>
              </Box>
              <Box my={1}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="overline" color="textSecondary">
                      <FormattedHTMLMessage {...translations.list.item.location} />
                    </Typography>
                    <Typography variant="body2" color="textPrimary">
                      {location?.name ?? '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="overline" color="textSecondary">
                      <FormattedHTMLMessage {...translations.list.item.shipmentCount} />
                    </Typography>
                    <Typography variant="body2" color="textPrimary">
                      {handlingList.shipment_ids?.length}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="overline" color="textSecondary">
                    <FormattedHTMLMessage {...translations.list.item.itemCount} />
                  </Typography>
                  <Typography variant="body2" color="textPrimary">
                    {handlingListData?.items?.length ?? 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="textSecondary">
                    <FormattedHTMLMessage {...translations.list.item.positions} />
                  </Typography>
                  <Typography variant="body2" color="textPrimary">
                    {positions?.length}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </CardActionArea>
        </Box>
      </Box>
    </Card>
  );
};

HandlingListCard.propTypes = {
  handlingList: PropTypes.object.isRequired,
};

export default HandlingListCard;
